import { useState } from 'react';

import { fullDate } from '../../../lib/datetime';
import type { BucketSerie, StatusAlvo } from '../../../api/monitoramento';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { BucketAmostrasDialog } from './BucketAmostrasDialog';

interface Props {
  buckets: BucketSerie[];
  /** Quantidade alvo de barras renderizadas. Buckets faltantes viram cinza. */
  totalBarras?: number;
  /** Fim da janela (instante do bucket mais recente). Default: agora. */
  fimJanela?: Date;
  /** Alvo dos buckets — usado para drill (Dialog de amostras) ao clicar numa barra. */
  alvo?: string;
}

const COR_BAR: Record<StatusAlvo, string> = {
  ATIVO: 'bg-green-500 hover:bg-green-600',
  DEGRADADO: 'bg-yellow-500 hover:bg-yellow-600',
  INDISPONIVEL: 'bg-red-500 hover:bg-red-600',
  SEM_DADOS: 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
};

/**
 * Uptime bars estilo Uptime Kuma: grade de divs coloridas representando
 * a saúde de um alvo ao longo do tempo. Sem lib de gráficos — apenas
 * Tailwind + Radix Tooltip (já presente no projeto).
 *
 * MVP fixa granularidade em `1h × 7d` = 168 barras, casando 1:1 com o
 * pré-agregado `integracao_amostra_horaria` do autenticador.
 *
 * Quando `alvo` é informado, clicar numa barra abre o `BucketAmostrasDialog`
 * com a lista de amostras cruas daquele bucket. Hover continua mostrando o
 * tooltip com os agregados (preview) — clique entrega a visão completa
 * (também serve de fallback para mobile, onde hover não existe).
 */
export function UptimeBars({ buckets, totalBarras = 168, fimJanela, alvo }: Props) {
  // Monta um mapa inicio_bucket (ISO, truncado à hora) → BucketSerie.
  const mapa = new Map<string, BucketSerie>();
  for (const b of buckets) {
    const iso = truncarHora(new Date(b.inicio_bucket)).toISOString();
    mapa.set(iso, b);
  }

  // Gera a sequência de horas em ordem cronológica (mais antigo → mais recente)
  // — garante layout estável mesmo quando o backend perde buckets intermediários.
  const agora = fimJanela ? new Date(fimJanela) : new Date();
  const fim = truncarHora(agora);
  const horas: { ts: Date; bucket: BucketSerie | null }[] = [];
  for (let i = totalBarras - 1; i >= 0; i -= 1) {
    const ts = new Date(fim);
    ts.setHours(ts.getHours() - i);
    const iso = ts.toISOString();
    horas.push({ ts, bucket: mapa.get(iso) ?? null });
  }

  const [bucketAberto, setBucketAberto] = useState<{ ts: Date; bucket: BucketSerie | null } | null>(
    null,
  );

  const drillHabilitado = !!alvo;
  const handleClick = (ts: Date, bucket: BucketSerie | null) => {
    if (!drillHabilitado) return;
    // Bucket cinza (SEM_DADOS) não tem amostras — abrir Dialog vazio só
    // gera frustração. Mantém o tooltip de hover ("Sem dados") como único
    // feedback nesse caso.
    if (!bucket || bucket.total === 0) return;
    setBucketAberto({ ts, bucket });
  };

  return (
    <>
      <TooltipProvider delayDuration={100}>
        <div className="flex gap-0.5">
          {horas.map(({ ts, bucket }, idx) => {
            const status: StatusAlvo = bucket?.status_dominante ?? 'SEM_DADOS';
            const cor = COR_BAR[status];
            const clicavel = drillHabilitado && !!bucket && bucket.total > 0;
            const cursor = clicavel ? 'cursor-pointer' : '';
            return (
              <Tooltip key={idx}>
                <TooltipTrigger asChild>
                  <div
                    role={clicavel ? 'button' : undefined}
                    tabIndex={clicavel ? 0 : -1}
                    onClick={() => handleClick(ts, bucket)}
                    onKeyDown={(e) => {
                      if (!clicavel) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick(ts, bucket);
                      }
                    }}
                    className={`h-8 w-1.5 rounded-sm transition-colors ${cor} ${cursor}`}
                    aria-label={`${fullDate(ts)}: ${status}${
                      clicavel ? ' — clique para ver amostras' : ''
                    }`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div className="space-y-0.5">
                    <div className="font-medium">{fullDate(ts)}</div>
                    {bucket ? (
                      <>
                        <div>Status: {status}</div>
                        <div>Total amostras: {bucket.total}</div>
                        <div>
                          Up: {bucket.count_up} · Degraded: {bucket.count_degraded} · Down:{' '}
                          {bucket.count_down}
                        </div>
                        {bucket.p95_ms !== null && <div>p95: {bucket.p95_ms} ms</div>}
                      </>
                    ) : (
                      <div className="italic text-gray-400">Sem dados</div>
                    )}
                    {clicavel && (
                      <div className="pt-1 text-[11px] italic text-gray-400">
                        Clique para ver amostras
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {alvo && (
        <BucketAmostrasDialog
          alvo={bucketAberto ? alvo : null}
          bucket={bucketAberto?.bucket ?? null}
          inicioBucket={bucketAberto ? bucketAberto.ts : null}
          fimBucket={bucketAberto ? new Date(bucketAberto.ts.getTime() + 60 * 60 * 1000) : null}
          open={bucketAberto !== null}
          onOpenChange={(open) => {
            if (!open) setBucketAberto(null);
          }}
        />
      )}
    </>
  );
}

function truncarHora(d: Date): Date {
  const c = new Date(d);
  c.setMinutes(0, 0, 0);
  return c;
}
